<script setup lang="ts">
import { ref, computed, watch, onActivated, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { setLocale, getLocale, type SupportedLocale } from '@/i18n'
import { useUserStore } from '@/stores/user'
import { useLikeStore } from '@/stores/like'
import { useCollectStore } from '@/stores/collect'
import { useHistoryStore } from '@/stores/history'
import { useWorksStore } from '@/stores/works'
import { ChevronRight, Flame, Languages, LogOut, User2 } from 'lucide-vue-next'
import DataFeedback from '@/components/data-feedback.vue'
import { showDialog, showToast } from 'vant'
import { Icon } from '@iconify/vue'
import IconAvatar from '@/assets/logo.svg'
import { useAppStore } from '@/stores/app'
import TheaterCard from './components/theater-card.vue'
import WorkCard from './components/work-card.vue'
import MediaPreview, { type MediaPreviewItem } from '@/components/media-preview'
import type { WorkRecord } from '@/types/apis/task'

const { t } = useI18n()

const currentLocale = ref<SupportedLocale>(getLocale())

const toggleLocale = () => {
  const next: SupportedLocale = currentLocale.value === 'zh-CN' ? 'en-US' : 'zh-CN'
  setLocale(next)
  currentLocale.value = next
  location.reload()
}

const router = useRouter()
const userStore = useUserStore()
const likeStore = useLikeStore()
const collectStore = useCollectStore()
const historyStore = useHistoryStore()
const worksStore = useWorksStore()
const consumedAuthVersion = ref(userStore.authVersion)

enum TabKey {
  Works = 'works',
  History = 'history',
  Favorite = 'favorite',
  Like = 'like',
}

interface Tab {
  key: TabKey
  i18nKey: string
}

const tabs: Tab[] = [
  { key: TabKey.Works, i18nKey: 'user.tabs.works' },
  { key: TabKey.History, i18nKey: 'user.tabs.history' },
  { key: TabKey.Favorite, i18nKey: 'user.tabs.favorite' },
  { key: TabKey.Like, i18nKey: 'user.tabs.likes' },
]

const activeTab = ref<TabKey>(TabKey.Works)
const previewVisible = ref(false)
const previewItems = ref<MediaPreviewItem[]>([])
const previewIndex = ref(0)

// 从 store 获取数据
const worksList = computed(() => worksStore.list)
const likeList = computed(() => likeStore.list)
const collectList = computed(() => collectStore.list)
const historyList = computed(() => historyStore.list)

// 获取当前 tab 的数据和加载状态
const getCurrentTabData = computed(() => {
  switch (activeTab.value) {
    case TabKey.Works:
      return {
        list: worksList.value,
        loading: worksStore.loading,
        hasMore: worksStore.hasMore,
        loaded: worksStore.loaded,
      }
    case TabKey.Like:
      return {
        list: likeList.value,
        loading: likeStore.loading,
        hasMore: likeStore.hasMore,
        loaded: likeStore.loaded,
      }
    case TabKey.Favorite:
      return {
        list: collectList.value,
        loading: collectStore.loading,
        hasMore: collectStore.hasMore,
        loaded: collectStore.loaded,
      }
    case TabKey.History:
      return {
        list: historyList.value,
        loading: historyStore.loading,
        hasMore: historyStore.hasMore,
        loaded: historyStore.loaded,
      }
    default:
      return {
        list: [],
        loading: false,
        hasMore: false,
        loaded: true,
      }
  }
})

const loadTabFirstPage = async (tab: TabKey) => {
  switch (tab) {
    case TabKey.Works:
      await worksStore.loadList(1)
      break
    case TabKey.Like:
      await likeStore.loadList(1)
      break
    case TabKey.Favorite:
      await collectStore.loadList(1)
      break
    case TabKey.History:
      await historyStore.loadList(1)
      break
  }
}

// 监听 tab 切换，首次加载数据
watch(
  activeTab,
  async tab => {
    if (!userStore.isLogin) return

    const tabData = getCurrentTabData.value
    if (tabData.loaded) return // 已加载过，不重复加载

    // 首次加载
    await loadTabFirstPage(tab)
  },
  { immediate: true },
)

// 加载更多
const onLoadMore = async () => {
  if (!userStore.isLogin) return

  const tabData = getCurrentTabData.value
  if (tabData.loading || !tabData.hasMore) return

  switch (activeTab.value) {
    case TabKey.Works:
      await worksStore.loadList()
      break
    case TabKey.Like:
      await likeStore.loadList()
      break
    case TabKey.Favorite:
      await collectStore.loadList()
      break
    case TabKey.History:
      await historyStore.loadList()
      break
  }
}

// 刷新当前 tab
const onRefresh = async () => {
  if (!userStore.isLogin) return
  await loadTabFirstPage(activeTab.value)
}

const refreshProfileAfterAuthChange = async () => {
  worksStore.clear()
  likeStore.clear()
  collectStore.clear()
  historyStore.clear()

  if (!userStore.isLogin) return

  await Promise.all([userStore.refreshProfileInfo(), loadTabFirstPage(activeTab.value)])
}

const handleTabClick = (tabKey: TabKey) => {
  activeTab.value = tabKey
}

const handleLogout = () => {
  showDialog({
    title: t('user.dialog.logoutTitle'),
    message: t('user.dialog.logoutMessage'),
    showCancelButton: true,
    confirmButtonColor: 'var(--color-purple-600)',
  }).then(action => {
    if (action === 'confirm') {
      userStore.logout()
    }
  })
}

const handleLogin = () => router.push('/login')
const handleMembershipClick = () => router.push('/plan')

const openWorksPreview = (targetWork: WorkRecord) => {
  if (!targetWork.results.videoUrl) {
    showToast(t('user.errors.noPreviewResource'))
    return
  }

  previewItems.value = [
    {
      id: String(targetWork.taskId),
      url: targetWork.results.videoUrl as string,
      type: 'video' as const,
      title: `作品 ${targetWork.taskId}`,
    },
  ]
  previewIndex.value = 0
  previewVisible.value = true
}

// 组件挂载时，如果已登录，预加载数据
onMounted(async () => {
  if (userStore.isLogin) {
    await Promise.all([
      !worksStore.loaded ? worksStore.loadList(1) : Promise.resolve(),
      !likeStore.loaded ? likeStore.loadList(1) : Promise.resolve(),
      !collectStore.loaded ? collectStore.loadList(1) : Promise.resolve(),
      !userStore.profileInfo ? userStore.refreshProfileInfo() : Promise.resolve(),
    ])
  }
})

onActivated(async () => {
  if (consumedAuthVersion.value === userStore.authVersion) return
  consumedAuthVersion.value = userStore.authVersion
  await refreshProfileAfterAuthChange()
})
</script>

<template>
  <div class="page-content relative h-screen pb-14 bg-gray-950 flex flex-col">
    <!-- 用户信息区域 -->
    <div class="bg-linear-to-bl from-purple-600/20 to-gray-950">
      <div class="flex flex-col gap-6 items-center pt-12 pb-6 justify-between px-4 pr-6">
        <div v-if="userStore.isLogin" class="flex items-center gap-4 w-full">
          <img
            :src="IconAvatar"
            class="relative shrink-0 bg-purple-600 rounded-full border-2 border-white/20 size-16 flex items-center justify-center overflow-hidden"
          />
          <div class="flex-1 flex flex-col justify-around w-full h-16">
            <div class="flex justify-between items-center w-full font-semibold text-white">
              <div class="text-xl line-clamp-1 flex-1">
                {{ userStore.profileInfo?.UserName || t('user.profile.defaultName') }}
              </div>

              <LogOut :size="20" class="opacity-60 -mr-2" @click="handleLogout" />
            </div>
            <div
              class="px-2 grow-0 w-fit cursor-pointer top-4 left-4 py-0.5 text-amber-200 flex items-center rounded-full border border-amber-300/20 bg-linear-to-r from-amber-300/18 via-white/10 to-sky-400/12 text-left text-xs"
              @click="handleMembershipClick"
            >
              <Flame :size="14" class="fill-amber-100" />
              <div class="flex items-center gap-1">
                {{ userStore.profileInfo?.AvailablePoints ?? 0 }}
              </div>
              <span class="h-3 w-px mx-2 bg-amber-50/20"></span>
              {{ userStore.profileInfo?.PlanName || t('user.profile.openMembership') }}
              <ChevronRight :size="16" />
            </div>
            <!-- <p class="opacity-60 text-sm">ID: {{ userStore.profileInfo?.UserId }}</p> -->
          </div>
        </div>
        <div v-else class="flex items-center gap-4 w-full">
          <div
            class="relative shrink-0 bg-white/20 rounded-full border-2 border-white/10 size-16 flex items-center justify-center overflow-hidden"
          >
            <User2 :size="26" />
          </div>
          <div class="flex-1 w-full">
            <div class="flex justify-between items-center w-full font-semibold text-white">
              <div class="text-xl line-clamp-1 flex-1">{{ t('user.profile.notLoggedIn') }}</div>
              <div class="flex items-center gap-2 cursor-pointer text-xs font-normal opacity-60" @click="handleLogin">
                {{ t('user.profile.goLogin') }}
                <ChevronRight :size="16" @click="handleLogin" />
              </div>
            </div>
          </div>
        </div>
        <div class="flex gap-4 justify-end w-full items-center">
          <a
            v-if="!useAppStore().isMobile"
            href="https://aiclip.acadsoc.com/home"
            target="_blank"
            class="bg-white/10 transition-colors duration-200 hover:border-purple-200/60 border border-transparent text-purple-200 text-xs h-9 pl-3 pr-2 flex items-center justify-between rounded-full"
          >
            <div class="flex items-center gap-3 text-sm">
              <img class="size-5" src="https://aiclip.acadsoc.com/assets/logo-Bl1zbcwM.svg" alt="" />
              {{ t('user.profile.createDrama') }}
            </div>
            <ChevronRight :size="22" class="ml-4" />
          </a>

          <!-- 语言切换 -->
          <div
            class="flex self-end items-center justify-between bg-white/5 rounded-full px-3 h-8 sm:h-9 cursor-pointer"
            @click="toggleLocale"
          >
            <span class="text-sm text-gray-300 mr-4"><Languages :size="18" /></span>
            <div class="flex items-center gap-1 text-sm">
              <span :class="currentLocale === 'zh-CN' ? 'text-white font-semibold' : 'text-gray-500'">中</span>
              <span class="text-gray-600 mx-0.5">/</span>
              <span :class="currentLocale === 'en-US' ? 'text-white font-semibold' : 'text-gray-500'">EN</span>
            </div>
          </div>
        </div>
      </div>
      <!-- Tab切换区域 -->
      <div class="">
        <div class="flex border-b border-gray-800 sm:gap-8 sm:px-4">
          <div
            v-for="tab in tabs"
            :key="tab.key"
            style="letter-spacing: 0.1em"
            class="py-4 w-full sm:w-auto text-base sm:text-xl relative text-center cursor-pointer"
            :class="[activeTab === tab.key ? 'text-white' : 'text-gray-400']"
            @click="handleTabClick(tab.key)"
          >
            {{ t(tab.i18nKey) }}
            <span
              v-if="activeTab === tab.key"
              class="h-0.5 w-5 rounded-full bg-purple-600 absolute bottom-0 left-1/2 transform -translate-x-1/2"
            ></span>
          </div>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-y-auto">
      <van-tabs
        v-model:active="activeTab"
        :show-header="false"
        swipeable
        :sticky="true"
        :offset-top="72"
        style="
          --van-tabs-bottom-bar-height: 0;
          --van-tabs-bottom-bar-color: var(--color-purple-600);
          --van-tabs-nav-background: var(--color-gray-950);
          --van-tab-active-text-color: var(--color-purple-500);
        "
      >
        <van-tab v-for="tab in tabs" :title="t(tab.i18nKey)" :key="tab.key" :name="tab.key">
          <!-- 未登录提示 -->
          <div v-if="!userStore.isLogin" class="text-center py-16 px-4">
            <div class="mb-6 flex items-center justify-center">
              <Icon class="text-8xl text-white/20" icon="solar:user-bold-duotone" />
            </div>
            <h3 class="text-xl text-white mb-2">{{ t('user.profile.loginPromptTitle') }}</h3>
            <p class="text-gray-500 mb-6">{{ t('user.profile.loginPromptDesc', { tabName: t(tab.i18nKey) }) }}</p>
            <button
              class="bg-purple-600 text-white px-4 py-2 mt-10 cursor-pointer rounded-full text-sm hover:bg-purple-700 transition-colors"
              @click="handleLogin"
            >
              {{ t('user.profile.loginNow') }}
            </button>
          </div>

          <!-- 已登录：显示列表 -->
          <van-pull-refresh v-else v-model="getCurrentTabData.loading" @refresh="onRefresh">
            <van-list
              v-model:loading="getCurrentTabData.loading"
              :finished="!getCurrentTabData.hasMore"
              :finished-text="t('common.messages.noMoreData')"
              @load="onLoadMore"
            >
              <!-- 作品列表 -->
              <div
                v-if="tab.key === TabKey.Works && activeTab === TabKey.Works && worksList.length > 0"
                class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4 p-4"
              >
                <WorkCard v-for="item in worksList" :key="item.taskId" :task="item" @click="openWorksPreview(item)" />
              </div>
              <!-- 历史记录列表 -->
              <div
                v-else-if="tab.key === TabKey.History && activeTab === TabKey.History && historyList.length > 0"
                class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-4 p-4"
              >
                <TheaterCard
                  v-for="record in historyList"
                  :key="record.EpisodeId"
                  @click="$router.push(`/theater/detail/${record.ScriptIdKey}?episode=${record.EpisodeId}`)"
                  :cover="record.EpisodeCoverImageUrl"
                  :label="record.ScriptName"
                  :description="
                    t('user.history.watchedTo', {
                      episode: record.SequenceNumber || 1,
                      progress: record.WatchProgress ? `（${record.WatchProgress?.toFixed(0)}%）` : '',
                    })
                  "
                />
              </div>
              <!-- 点赞 -->
              <div
                v-else-if="tab.key === TabKey.Like && activeTab === TabKey.Like && likeList.length > 0"
                class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 p-4"
              >
                <TheaterCard
                  v-for="item in likeList"
                  :key="item.IdKey"
                  @click="$router.push(`/theater/detail/${item.ScriptIdKey}?episode=${item.EpisodeId}`)"
                  :cover="item.ScriptCoverImageUrl"
                  :label="item.ScriptName"
                  :total-episodes="item.TotalEpisodes"
                  :description="t('user.like.episode', { episode: item.SequenceNumber })"
                />
              </div>
              <!-- 收藏列表 -->
              <div
                v-else-if="tab.key === TabKey.Favorite && activeTab === TabKey.Favorite && collectList.length > 0"
                class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 p-4"
              >
                <TheaterCard
                  v-for="item in collectList"
                  :key="item.IdKey"
                  @click="$router.push(`/theater/detail/${item.ScriptIdKey}`)"
                  :cover="item.ScriptCoverImageUrl"
                  :label="item.ScriptName"
                  :total-episodes="item.TotalEpisodes"
                />
              </div>

              <!-- 空状态 -->
              <div v-if="getCurrentTabData.list.length === 0 && !getCurrentTabData.loading">
                <DataFeedback :empty="true" :loading="false" :finished="false" />
              </div>
            </van-list>
          </van-pull-refresh>
        </van-tab>
      </van-tabs>

      <!-- 底部版权信息 -->
      <div class="text-center text-gray-600 text-xs py-8">@{{ $t('login.page.title') }} 2026 V1.0.0</div>
    </div>

    <MediaPreview v-model:visible="previewVisible" :items="previewItems" :initial-index="previewIndex" />
  </div>
</template>

<style scoped>
/* 隐藏滚动条 */
::-webkit-scrollbar {
  display: none;
}
</style>
